import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "sonner";

import "@/App.css";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import DashboardLayout from "@/components/DashboardLayout";
import HomeTickets from "@/pages/HomeTickets";
import Dashboard from "@/pages/Dashboard";
import BookingFlow from "@/pages/BookingFlow";
import Orders from "@/pages/Orders";
import RoutesPage from "@/pages/RoutesPage";
import Customers from "@/pages/Customers";
import CashRegister from "@/pages/CashRegister";
import QrVerify from "@/pages/QrVerify";
import Staff from "@/pages/Staff";
import Payments from "@/pages/Payments";
import Plans from "@/pages/Plans";
import Subscription from "@/pages/Subscription";
import Settings from "@/pages/Settings";
import Accounting from "@/pages/Accounting";
import PublicMenu from "@/pages/PublicMenu";
import PublicBookingFlow from "@/pages/PublicBookingFlow";
import SuperAdminLayout from "@/components/SuperAdminLayout";
import SuperAdminDashboard from "@/pages/superadmin/Dashboard";
import SuperAdminCompanies from "@/pages/superadmin/Companies";
import SuperAdminPlans from "@/pages/superadmin/Plans";
import SuperAdminCityPricing from "@/pages/superadmin/CityPricing";
import SuperAdminAdmins from "@/pages/superadmin/Admins";
import SuperAdminAudit from "@/pages/superadmin/Audit";
import SuperAdminUsers from "@/pages/superadmin/Users";
import SuperAdminTickets from "@/pages/superadmin/Tickets";
import SuperAdminAnnouncements from "@/pages/superadmin/Announcements";
import SuperAdminSubscriptions from "@/pages/superadmin/Subscriptions";
import SuperAdminTransactions from "@/pages/superadmin/Transactions";
import SuperAdminPaymentProviders from "@/pages/superadmin/PaymentProviders";
import NotFound from "@/pages/NotFound";

function AuthenticatedGate({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-slate-500">Chargement…</div>;
  if (user) {
    // Super admin (propriétaire du SaaS) → console dédiée, jamais /app
    const target = user.role === "super_admin" ? "/superadmin" : "/app";
    return <Navigate to={target} replace />;
  }
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ErrorBoundary>
            <Toaster position="top-right" richColors closeButton />
            <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/book/:companyId" element={<PublicMenu />} />
            <Route path="/book/:companyId/:kind" element={<PublicBookingFlow />} />
            <Route path="/login" element={<AuthenticatedGate><Login /></AuthenticatedGate>} />
            <Route path="/register" element={<AuthenticatedGate><Register /></AuthenticatedGate>} />

            <Route path="/app" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<HomeTickets />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="book/:kind" element={<BookingFlow />} />
              <Route path="orders" element={<Orders />} />
              <Route path="routes" element={<RoutesPage />} />
              <Route path="customers" element={<Customers />} />
              <Route path="cash" element={<CashRegister />} />
              <Route path="accounting" element={<Accounting />} />
              <Route path="qr" element={<QrVerify />} />
              <Route path="staff" element={<Staff />} />
              <Route path="payments" element={<Payments />} />
              <Route path="plans" element={<Plans />} />
              <Route path="subscription" element={<Subscription />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            <Route path="/superadmin" element={<ProtectedRoute role="super_admin"><SuperAdminLayout /></ProtectedRoute>}>
              <Route index element={<SuperAdminDashboard />} />
              <Route path="companies" element={<SuperAdminCompanies />} />
              <Route path="plans" element={<SuperAdminPlans />} />
              <Route path="city-pricing" element={<SuperAdminCityPricing />} />
              <Route path="super-admins" element={<SuperAdminAdmins />} />
              <Route path="users" element={<SuperAdminUsers />} />
              <Route path="tickets" element={<SuperAdminTickets />} />
              <Route path="announcements" element={<SuperAdminAnnouncements />} />
              <Route path="subscriptions" element={<SuperAdminSubscriptions />} />
              <Route path="transactions" element={<SuperAdminTransactions />} />
              <Route path="payment-providers" element={<SuperAdminPaymentProviders />} />
              <Route path="audit" element={<SuperAdminAudit />} />
            </Route>

            <Route path="*" element={<NotFound />} />
            </Routes>
          </ErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
